import type { ErrorLike } from '@apollo/client'
import { useApolloClient } from '@apollo/client/react'
import { useTranslate } from '@athena/ui'
import { useBlocker } from '@tanstack/react-router'
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'

import { SaveAttemptDocument } from '../../graphql/generated/operations.js'
import { apiErrorCode } from '../../lib/errors.js'
import { useFirstUserErrorText } from '../../lib/user-errors.js'

import type { SubmittedAnswerPayload } from './answers.js'
import {
  RETRY_DELAYS,
  createAnswerSaver,
  type ChangeKind,
  type SaveOutcome,
  type SaverState,
} from './answerSaver.js'
import { invalidateStudentActivity } from './cache.js'

/**
 * How the runner keeps answers on the server.
 *
 * `continuous` is an untimed practice: every answer is saved as the student
 * gives it, a failure is said on screen and retried, and leaving with an answer
 * the server has not confirmed asks first.
 *
 * `onNext` is a timed practice, exactly as it has always behaved: a save each
 * time the student moves on, quietly, because the deadline hands in whatever
 * was saved and the next question saves again. It shares the queue — one
 * request at a time, newest answers only — and nothing else changes for it.
 */
export type AutosaveMode = 'continuous' | 'onNext'

export interface AttemptProgress {
  answers: SubmittedAnswerPayload[]
  currentQuestionPosition: number
}

export interface UseAttemptAutosaveOptions {
  attemptId: string
  /** The answers and the question the student is on, read when a request is sent. */
  read: () => AttemptProgress
  mode: AutosaveMode
  /** Off until the saved answers are in place, and once the attempt is finished. */
  enabled: boolean
}

export interface AttemptAutosave {
  state: SaverState
  /** The state now, for code that runs after an `await` rather than in a render. */
  getState: () => SaverState
  isSaved: () => boolean
  changed: (kind: ChangeKind) => void
  flush: () => Promise<boolean>
  /**
   * What "Save & close" waits for: every answer, and the question the student
   * is on even if no answer changed, so the home screen offers the right one.
   */
  saveBeforeLeaving: () => Promise<boolean>
  retry: () => void
  pause: () => void
  resume: () => void
  /** Set while the leave guard is holding a navigation for the student to decide on. */
  leaving: { proceed: () => void; stay: () => void } | null
}

/**
 * Codes that say asking again will not help: the session is gone, the attempt
 * is not this student's, or the request was malformed. A network error, or the
 * API's own `INTERNAL`, might well work a moment later.
 */
const PERMANENT_CODES: ReadonlySet<string> = new Set([
  'UNAUTHENTICATED',
  'FORBIDDEN',
  'NOT_FOUND',
  'BAD_USER_INPUT',
])

/**
 * How long leaving waits for an outstanding save before asking. Long enough
 * for an ordinary request to come back, so a student who answered and pressed
 * the browser's back button a moment later is simply let go.
 */
export const LEAVE_WAIT_MS = 3000

export function useAttemptAutosave(options: UseAttemptAutosaveOptions): AttemptAutosave {
  const client = useApolloClient()
  const t = useTranslate()
  const userErrorText = useFirstUserErrorText()

  // Everything the saver calls back into, kept current so the saver itself is
  // created once and survives every render.
  const latest = useRef({ ...options, t, userErrorText })
  useLayoutEffect(() => {
    latest.current = { ...options, t, userErrorText }
  })

  /** The position in the last save the server confirmed. */
  const confirmedPosition = useRef<number | null>(null)
  /** Whether anything was saved during this visit, so the home screen reloads on the way out. */
  const savedThisVisit = useRef(false)
  const [state, setState] = useState<SaverState>({ status: 'saved', failure: null })

  const [saver] = useState(() =>
    createAnswerSaver({
      save: async (): Promise<SaveOutcome> => {
        const { attemptId, read } = latest.current
        const { answers, currentQuestionPosition } = read()
        try {
          // `client.mutate` rather than `useMutation`: nothing on screen
          // renders a request's loading state, so there is no reason for every
          // save to re-render the runner twice.
          const result = await client.mutate({
            mutation: SaveAttemptDocument,
            variables: { attemptId, answers, currentQuestionPosition },
          })
          const payload = result.data?.saveAttempt
          if (payload?.attempt) {
            confirmedPosition.current = currentQuestionPosition
            savedThisVisit.current = true
            return { ok: true }
          }
          // The server refused, and said why: time is up, the practice has
          // closed. Sending the same thing again would be refused again.
          return {
            ok: false,
            retryable: false,
            message: latest.current.userErrorText(
              payload?.userErrors,
              latest.current.t('attempt.saveFailed'),
            ),
          }
        } catch (error) {
          const code = apiErrorCode(error as ErrorLike)
          if (code !== undefined && PERMANENT_CODES.has(code)) {
            return { ok: false, retryable: false, message: latest.current.t('attempt.saveFailed') }
          }
          return { ok: false, retryable: true }
        }
      },
      onState: setState,
      policy: () =>
        latest.current.mode === 'continuous'
          ? { saveOnChange: true, retryDelays: RETRY_DELAYS }
          : { saveOnChange: false, retryDelays: [] },
    }),
  )

  const changed = useCallback(
    (kind: ChangeKind) => {
      if (latest.current.enabled) saver.changed(kind)
    },
    [saver],
  )

  const flush = useCallback(
    () => (latest.current.enabled ? saver.flush() : Promise.resolve(true)),
    [saver],
  )

  const saveBeforeLeaving = useCallback(() => {
    if (!latest.current.enabled) return Promise.resolve(true)
    if (latest.current.read().currentQuestionPosition !== confirmedPosition.current) {
      saver.changed('move')
    }
    return saver.flush()
  }, [saver])

  // Leaving the runner by any route — Save & close, the browser's back button,
  // a finished submission — sends what has not gone yet rather than dropping
  // it, then stops scheduling for a screen that is no longer there. The home
  // screen's lists are cached, so they are reloaded once that save lands.
  useEffect(() => {
    saver.start()
    return () => {
      const last =
        saver.isSaved() || !latest.current.enabled ? Promise.resolve(true) : saver.flush()
      saver.stop()
      void last.then(() => {
        if (savedThisVisit.current) invalidateStudentActivity(client.cache)
      })
    }
  }, [saver, client])

  // An untimed practice watches the connection and the page: a save that
  // failed offline is tried again the moment the browser is back online, and
  // a student switching away — which on a phone can be the last thing the tab
  // ever does — sends what is outstanding first.
  const { mode, enabled } = options
  useEffect(() => {
    if (mode !== 'continuous' || !enabled) return

    const onOnline = () => {
      if (saver.getState().failure?.retryable) saver.retry()
    }
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') void saver.flush()
    }
    const onPageHide = () => {
      void saver.flush()
    }
    window.addEventListener('online', onOnline)
    document.addEventListener('visibilitychange', onVisibility)
    window.addEventListener('pagehide', onPageHide)
    return () => {
      window.removeEventListener('online', onOnline)
      document.removeEventListener('visibilitychange', onVisibility)
      window.removeEventListener('pagehide', onPageHide)
    }
  }, [mode, enabled, saver])

  /**
   * The leave guard, for an untimed practice only.
   *
   * Nothing to ask about when every answer is saved, which is almost always —
   * so leaving normally asks nothing. With a save outstanding, leaving waits
   * for it and goes if it lands; it asks only when the answer is still not on
   * the server, which in practice means the connection is down.
   *
   * The browser's own prompt covers a reload or a closed tab, where nothing
   * can be waited for: it appears only while something is unsaved.
   */
  const shouldBlockFn = useCallback(async () => {
    if (saver.isSaved()) return false
    let timer: ReturnType<typeof setTimeout> | undefined
    const saved = await Promise.race([
      saver.flush(),
      new Promise<boolean>((resolve) => {
        timer = setTimeout(() => resolve(false), LEAVE_WAIT_MS)
      }),
    ])
    clearTimeout(timer)
    return !saved
  }, [saver])
  const enableBeforeUnload = useCallback(() => !saver.isSaved(), [saver])
  const blocker = useBlocker({
    shouldBlockFn,
    enableBeforeUnload,
    disabled: mode !== 'continuous' || !enabled,
    withResolver: true,
  })

  return {
    state,
    getState: saver.getState,
    isSaved: saver.isSaved,
    changed,
    flush,
    saveBeforeLeaving,
    retry: saver.retry,
    pause: saver.pause,
    resume: saver.resume,
    leaving:
      blocker.status === 'blocked' ? { proceed: blocker.proceed, stay: blocker.reset } : null,
  }
}
